import React, { useState } from 'react';

import I18n from 'utils/I18n';
import Cldr from 'utils/Cldr';
import clsx from '@strava/ui/clsx';
import Button from '@strava/ui/Button';
import SubscriptionFeatures from '../shared/SubscriptionFeatures';
import DurationSelectTrial from './DurationSelect';
import DurationSelect from '../Summary/DurationSelect';
import Card from '../shared/Card';
import Inset from '../shared/Inset';

import { getOrganicProduct } from '../../state/Checkout.actions';
import { useCheckoutContext } from '../../state/CheckoutContext';
import { I18nPrefix } from '../../shared/constants';

import styles from './SummaryTrials.scss';

const I18nPrefixTrial = `${I18nPrefix}.summary.trial`;

export const calculateTrialEndDate = (trialLength, startDate) => {
  const trialEndDate = startDate ? new Date(startDate) : new Date();
  trialEndDate.setDate(trialEndDate.getDate() + trialLength);
  return Cldr.formatDateTime(trialEndDate, {
    format: 'additional',
    type: 'yMMMd'
  });
};

export const SummaryTrials = () => {
  const { checkoutEntityState, dispatch } = useCheckoutContext();

  const { duration, experiments, trialInfo, products } = checkoutEntityState;
  const cartData = products?.[duration];
  const {
    shouldShowTrustLanguage,
    shouldShowFullSizedCta,
    shouldShowBothPlansByDefault
  } = experiments;

  const [showMoreBillingOptions, setShowMoreBillingOptions] = useState(false);

  const toggleShowBillingOptions = () =>
    setShowMoreBillingOptions(!showMoreBillingOptions);

  const isYearlyDuration = cartData.duration === 'yearly';

  const totalPrimaryPrice = isYearlyDuration
    ? cartData.yearlyTotal
    : cartData.monthlyTotal;

  const renderPerMonthPrice = (price, currency) =>
    I18n.t_html(`${I18nPrefixTrial}.per_month`, {
      price: Cldr.formatCurrency(price, currency)
    });

  const renderPerYearPrice = (price, currency) =>
    I18n.t_html(`${I18nPrefixTrial}.per_year`, {
      price: Cldr.formatCurrency(price, currency)
    });

  const renderOverview = () => {
    if (shouldShowTrustLanguage && trialInfo?.trialLength) {
      return (
        <span data-testid="disclaimer">
          {I18n.t_html(`${I18nPrefixTrial}.disclaimer`, {
            trialEndDate: calculateTrialEndDate(trialInfo.trialLength)
          })}
        </span>
      );
    }
    return <SubscriptionFeatures />;
  };

  const { currency } = cartData;

  const totalPrimaryText = isYearlyDuration
    ? renderPerYearPrice(totalPrimaryPrice, currency)
    : renderPerMonthPrice(totalPrimaryPrice, currency);

  const billingOptionsButtonText = I18n.t(
    `${I18nPrefixTrial}.${
      !showMoreBillingOptions ? 'more_billing_options' : 'less_billing_options'
    }`
  );
  return (
    <div className={styles.checkoutSummary}>
      <Card dataTestId="card">
        <Inset className={styles.overviewSection}>
          <h4 className={styles.overviewTitle}>
            {I18n.t(`${I18nPrefixTrial}.title`, {
              days: trialInfo?.trialLength
            })}
          </h4>
          <p className={styles.overviewDescription}>{renderOverview()}</p>
        </Inset>

        {shouldShowBothPlansByDefault && <DurationSelect />}

        <Inset className={styles.pricingSection} dataTestId="pricing-section">
          <div className={clsx(styles.lineItem, styles.billedToday)}>
            <div className={styles.priceRow}>
              <span>{I18n.t(`${I18nPrefixTrial}.billed.today`)}</span>
              <span>
                {Cldr.formatCurrency(cartData.billedToday, cartData.currency)}
              </span>
            </div>
          </div>

          <div
            className={clsx(styles.lineItem, styles.billedAfterTrial)}
            data-testid="billed-after-trial"
          >
            <div className={styles.priceRow}>
              <span>{I18n.t(`${I18nPrefixTrial}.billed.after_trial`)}</span>
              <span>{totalPrimaryText}</span>
            </div>
            <div
              className={clsx(
                styles.moreBillingOptions,
                shouldShowFullSizedCta && styles.fullSizedCtaContainer
              )}
            >
              {!shouldShowBothPlansByDefault && shouldShowFullSizedCta && (
                <Button
                  className={styles.moreBillingOptionsButton}
                  onClick={toggleShowBillingOptions}
                  type="button"
                  variant="primary-outline"
                >
                  {billingOptionsButtonText}
                </Button>
              )}
              {!shouldShowBothPlansByDefault && !shouldShowFullSizedCta && (
                <button
                  className={clsx(
                    'btn',
                    'btn-link',
                    styles.moreBillingOptionsButton
                  )}
                  type="button"
                  onClick={toggleShowBillingOptions}
                >
                  {billingOptionsButtonText}
                </button>
              )}
              {!shouldShowBothPlansByDefault && (
                <span className={styles.actionDivider}>
                  {shouldShowFullSizedCta ? '' : '·'}
                </span>
              )}
              <button
                className={clsx(
                  'btn',
                  'btn-link',
                  styles.moreBillingOptionsButton
                )}
                onClick={() => getOrganicProduct(checkoutEntityState, dispatch)}
                type="button"
              >
                {I18n.t(`${I18nPrefixTrial}.add_promo_code`)}
              </button>
            </div>
          </div>

          {!shouldShowBothPlansByDefault && showMoreBillingOptions && (
            <DurationSelectTrial />
          )}
        </Inset>

        <Inset className={styles.footnoteSection}>
          {I18n.t(`${I18nPrefixTrial}.cancel`)}
        </Inset>
      </Card>
    </div>
  );
};

export default SummaryTrials;
